import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  CancelVibeDeploymentBuildPathVarsSchema,
  CancelVibeDeploymentBuildResponseReadSchema,
  GetVibeDeploymentPathVarsSchema,
  GetVibeDeploymentResponseSchema,
  GetVibeDeployStatePathVarsSchema,
  GetVibeDeployStateQuerySchema,
  GetVibeDeployStateResponseSchema,
  GetVibeRefPathVarsSchema,
  GetVibeRefQuerySchema,
  GetVibeRefResponseSchema,
  ListVibeDeploymentsPathVarsSchema,
  ListVibeDeploymentsResponseSchema,
  ListVibeRefsPathVarsSchema,
  ListVibeRefsResponseSchema,
  TriggerVibeDeploymentBodySchema,
  TriggerVibeDeploymentPathVarsSchema,
  TriggerVibeDeploymentResponseReadSchema
} from "../schemas";

export const VibeDeploymentEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeDeployment — one push→build→deploy attempt per row
  // ---------------------------------------------------------------------------

  /**
   * Trigger a new deployment. Creates a VibeDeployment row in BUILDING
   * and a sibling VibeBuildJob row in PENDING in a single transaction —
   * the build runner picks the job up out-of-band. 404 if the app does
   * not exist, 409 if the app has no provisioned repository yet
   * (a deploy without a repo is a category error).
   *
   * The response is a union discriminated on `status`, and BOTH arms are
   * 2xx:
   *   - `created` — the deployment + build job (+ approval request when the
   *     app gates prod deploys) were written.
   *   - `confirmation_required` — the org is over its usage SOFT cap.
   *     NOTHING was written, not even a version number. Re-send the same
   *     request with `confirmOverage: true` to proceed. This is a question,
   *     never a refusal: it is deliberately NOT a 4xx, because an org over a
   *     soft limit is asked, not blocked. Usage-vs-cap detail for rendering
   *     the prompt comes from `GET /api/vibe/cost-safety`.
   *
   * The HARD stop is unchanged and separate: an admin-SUSPENDED org gets
   * 403, and `confirmOverage` does not bypass it.
   */
  TriggerDeployment: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/deployments",
    PathVars: TriggerVibeDeploymentPathVarsSchema,
    Body: TriggerVibeDeploymentBodySchema,
    Response: ApiSuccessSchema(TriggerVibeDeploymentResponseReadSchema)
  },

  /**
   * List an app's deployments newest-first. Soft-deleted rows are
   * excluded; in-flight rows (BUILDING / DEPLOYING / AWAITING_APPROVAL)
   * appear ahead of terminal rows because creation time monotonically
   * increases for any given app.
   */
  ListDeployments: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/deployments",
    PathVars: ListVibeDeploymentsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeDeploymentsResponseSchema)
  },

  /**
   * Fetch one deployment with its sibling build job inlined. 404 if the
   * deployment id is not owned by the given app + org pair (cross-tenant
   * lookups are indistinguishable from missing).
   */
  GetDeployment: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/deployments/:deploymentId",
    PathVars: GetVibeDeploymentPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeDeploymentResponseSchema)
  },

  /**
   * Stop a deployment's build while it is queued or running. The build job and
   * the deployment go CANCELLED together, beside a DEPLOYMENT_CANCELLED audit
   * row naming who asked, and the tenant agent is woken to stop the executor
   * build on its next pass.
   *
   * `outcome: "already_ended"` is a 200, not an error: the build had already
   * ended (or the deployment had left BUILDING), nothing was written, and the
   * rows come back as they stand. 404 when the deployment is not the given
   * app's in the caller's org — cross-tenant lookups are indistinguishable from
   * missing.
   */
  CancelDeploymentBuild: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/deployments/:deploymentId/cancel",
    PathVars: CancelVibeDeploymentBuildPathVarsSchema,
    Response: ApiSuccessSchema(CancelVibeDeploymentBuildResponseReadSchema)
  },

  /**
   * "Did my push land, and what became of it" — in ONE call, keyed by the
   * commit or the ref rather than by a deployment id the caller does not have.
   *
   * The endpoint an automated client should reach for after `git push`.
   * Everything it answers is inferable today only from `git`'s own
   * stdout/stderr, which cannot be classified reliably: rejection lines print
   * first and are destroyed by a pipe through `tail`, `-q` silences the
   * success report but not the failure report, a backgrounded push carries no
   * outcome at all, and an error quoting a remote URL has the same shape as a
   * success report. Failures therefore read as successes, which is the
   * direction that hurts — a verification obligation gets armed for a deploy
   * that never happened.
   *
   * Deliberately NOT a filter on `ListDeployments`. A list keyed by ref could
   * only ever return deployments, and "no deployment" is precisely the
   * ambiguous answer — it means "your push never arrived" OR "it arrived and
   * nothing was configured to deploy it". The `outcome` discriminator is the
   * contract; the rows are the supporting detail.
   *
   * It also carries `served` — what is answering the app's URL right now,
   * read from the control plane. That half is not duplicated from the
   * `X-Vibe-Deployment` response header on purpose: the header is stamped
   * after the edge authorizer, so a PRIVATE app's 401 carries none, and
   * PRIVATE is the default.
   *
   * 404 when the app is not owned by the caller's org.
   */
  GetDeployState: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/deploy-state",
    PathVars: GetVibeDeployStatePathVarsSchema,
    Query: GetVibeDeployStateQuerySchema,
    Response: ApiSuccessSchema(GetVibeDeployStateResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeRef — branch / tag heads with CAS-update semantics
  // ---------------------------------------------------------------------------

  /**
   * List branch + tag heads for an app's repository. 404 if the app is
   * not in the caller's org. Returns an empty array when the repo has
   * no refs yet (freshly-provisioned, never-pushed).
   */
  ListRefs: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/refs",
    PathVars: ListVibeRefsPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeRefsResponseSchema)
  },

  /**
   * Fetch one ref by its fully-qualified name (e.g. `refs/heads/main`).
   * Query parameter (rather than path parameter) because ref names
   * contain slashes and would otherwise force an awkward wildcard
   * route definition. 404 if the ref does not exist.
   */
  GetRef: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/refs/find",
    PathVars: GetVibeRefPathVarsSchema,
    Query: GetVibeRefQuerySchema,
    Response: ApiSuccessSchema(GetVibeRefResponseSchema)
  }
} as const;
